import { useEffect, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Modal } from "components/ui/modals/Modal/Modal"
import { Form } from "antd"
import { Input } from "components/ui/inputs/Input"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { H3 } from "components/ui/typography/titles/Titles"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { DimensionInputs } from "components/ui/inputs/DimensionInputs/DimensionInputs"
import { useUpdateModelNumberDimensions } from "hooks/api/modelNumbers/modelNumbers.hook"
import { useMessage } from "hooks/message.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import type { TDimensions } from "types/modelNumbers.type"
import style from "./EditDimensionsModal.module.scss"

const { title, wrapper, dimensionsBlock, formItem, buttonsBlock, input } = style

type Props = {
	modelNumberId?: number
	dimensions: TDimensions
	isModalOpen: boolean
	setModalClose: () => void
	onSave: (dimensions: TDimensions) => void
}

export const EditDimensionsModal = ({ modelNumberId, dimensions, isModalOpen, setModalClose, onSave }: Props) => {
	const queryClient = useQueryClient()
	const { success } = useMessage()
	const { formatError } = useApiErrorFormatter()
	const { mutate: updateModelNumberDimensions, isPending: isUpdateModelNumberDimensionsPending } =
		useUpdateModelNumberDimensions()
	const [draft, setDraft] = useState<TDimensions>(dimensions)

	useEffect(() => {
		if (isModalOpen) {
			setDraft(dimensions)
		}
	}, [isModalOpen, dimensions])

	const addHandle = (field: keyof Pick<TDimensions, "width" | "height" | "depth">, value: string) => {
		setDraft((prev) => ({ ...prev, [field]: [...prev[field], value] }))
	}

	const removeHandle = (field: keyof Pick<TDimensions, "width" | "height" | "depth">, value: string) => {
		setDraft((prev) => ({ ...prev, [field]: prev[field].filter((v) => v !== value) }))
	}

	const saveHandle = () => {
		if (!modelNumberId) return

		updateModelNumberDimensions(
			{ id: modelNumberId, dimensions: draft },
			{
				onSuccess: () => {
					onSave(draft)
					void Promise.all([queryClient.invalidateQueries({ queryKey: ["getModelNumbers"] })])
					queryClient.invalidateQueries({ queryKey: ["getModelNumber", modelNumberId] })
					success("Model number saved")
					setModalClose()
				},
				onError: (e) => {
					formatError(e)
				},
			},
		)
	}

	const cancelHandle = () => {
		setDraft(dimensions)
		setModalClose()
	}

	return (
		<Modal width={488} isModalOpen={isModalOpen} setModalClose={cancelHandle}>
			<H3 className={title}>Edit dimensions</H3>
			<Form layout="vertical">
				<div className={wrapper}>
					<DimensionInputs
						label="Width, in"
						values={draft.width}
						onAdd={(value) => addHandle("width", value)}
						onRemove={(value) => removeHandle("width", value)}
					/>
					<DimensionInputs
						label="Height, in"
						values={draft.height}
						onAdd={(value) => addHandle("height", value)}
						onRemove={(value) => removeHandle("height", value)}
					/>
					<DimensionInputs
						label="Depth, in"
						values={draft.depth}
						onAdd={(value) => addHandle("depth", value)}
						onRemove={(value) => removeHandle("depth", value)}
					/>
					<div className={dimensionsBlock}>
						<span>
							Avg size, in<sup>3</sup>
						</span>
						<Form.Item className={formItem}>
							<Input
								className={input}
								value={draft.avg_size}
								onChange={(e) => setDraft((prev) => ({ ...prev, avg_size: e.target.value }))}
							/>
						</Form.Item>
					</div>
					<div className={buttonsBlock}>
						<ButtonSecondary onClick={cancelHandle}>Cancel</ButtonSecondary>
						<ButtonPrimary onClick={saveHandle} submittable={!isUpdateModelNumberDimensionsPending}>
							Save
						</ButtonPrimary>
					</div>
				</div>
			</Form>
		</Modal>
	)
}
